import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import { formatDistanceToNow, format } from 'date-fns';

const PRIORITY_OPTS  = ['low', 'normal', 'high', 'urgent'];
const STATUS_OPTS    = ['open', 'in_progress', 'resolved'];
const PRIORITY_BADGE = { low:'badge-green', normal:'badge-yellow', high:'badge-orange', urgent:'badge-red' };
const STATUS_BADGE   = { open:'badge-red', in_progress:'badge-blue', resolved:'badge-green' };
const PRIORITY_ICON  = { low:'🟢', normal:'🟡', high:'🟠', urgent:'🔴' };

const EMPTY = {
  customer_name: '', customer_email: '', customer_whatsapp: '',
  title: '', description: '', priority: 'normal',
  status: 'open', resolution_notes: '',
};

export default function Issues() {
  const [issues,     setIssues]    = useState([]);
  const [loading,    setLoading]   = useState(true);
  const [filter,     setFilter]    = useState('all'); // all | open | in_progress | resolved
  const [modal,      setModal]     = useState(null);
  const [form,       setForm]      = useState(EMPTY);
  const [saving,     setSaving]    = useState(false);
  const [expanded,   setExpanded]  = useState(null);
  const [toast,      setToast]     = useState('');

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 3000); };

  const load = useCallback(async () => {
    setLoading(true);
    let q = supabase.from('issues').select('*').order('created_at', { ascending: false });
    if (filter !== 'all') q = q.eq('status', filter);
    const { data } = await q;
    setIssues(data || []);
    setLoading(false);
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const openAdd  = () => { setForm(EMPTY); setModal({ mode: 'add' }); };
  const openEdit = (i) => { setForm({ ...i }); setModal({ mode: 'edit', id: i.id }); };

  const save = async () => {
    if (!form.title.trim() || !form.customer_name.trim()) {
      return showToast('Title and customer name are required');
    }
    setSaving(true);
    const payload = { ...form };
    delete payload.id;
    delete payload.created_at;
    delete payload.resolved_at;
    if (form.status === 'resolved' && modal.mode === 'edit') {
      payload.resolved_at = new Date().toISOString();
    }
    if (modal.mode === 'add') {
      const { error } = await supabase.from('issues').insert(payload);
      if (error) showToast('❌ ' + error.message);
      else showToast('Issue created ✅');
    } else {
      const { error } = await supabase.from('issues').update(payload).eq('id', modal.id);
      if (error) showToast('❌ ' + error.message);
      else showToast('Issue updated ✅');
    }
    setSaving(false);
    setModal(null);
    load();
  };

  const quickStatus = async (id, status) => {
    const update = { status };
    if (status === 'resolved') update.resolved_at = new Date().toISOString();
    await supabase.from('issues').update(update).eq('id', id);
    setIssues(prev => prev.map(i => i.id === id ? { ...i, ...update } : i));
    showToast(status === 'resolved' ? '✅ Resolved!' : 'Status updated');
  };

  const deleteIssue = async (id) => {
    await supabase.from('issues').delete().eq('id', id);
    setIssues(prev => prev.filter(i => i.id !== id));
    showToast('Issue deleted');
  };

  const counts = {
    open:        issues.filter(i => i.status === 'open').length,
    in_progress: issues.filter(i => i.status === 'in_progress').length,
    resolved:    issues.filter(i => i.status === 'resolved').length,
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 10 }}>
        <h1 className="page-title" style={{ marginBottom: 0 }}>
          🎫 Issues
          {counts.open > 0 && <span className="badge badge-red" style={{ marginLeft: 10 }}>{counts.open} open</span>}
        </h1>
        <button className="btn" style={{ marginTop: 0 }} onClick={openAdd}>+ New Issue</button>
      </div>

      {/* Filter tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 18 }}>
        {[
          { key: 'all',         label: `All (${issues.length})` },
          { key: 'open',        label: `🔴 Open (${counts.open})` },
          { key: 'in_progress', label: `🔵 In Progress (${counts.in_progress})` },
          { key: 'resolved',    label: `✅ Resolved (${counts.resolved})` },
        ].map(f => (
          <button key={f.key} className="btn"
            style={{
              background: filter === f.key ? '#0ea5e9' : '#e2e8f0',
              color:      filter === f.key ? '#fff'    : '#374151',
              marginTop: 0, fontSize: 12, padding: '6px 14px',
            }}
            onClick={() => setFilter(f.key)}>
            {f.label}
          </button>
        ))}
      </div>

      {/* Issues list */}
      {loading ? (
        <div className="card" style={{ color: '#64748b', fontSize: 13 }}>Loading…</div>
      ) : issues.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: 48 }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>🎉</div>
          <div style={{ fontWeight: 600, color: '#374151', marginBottom: 6 }}>
            {filter === 'all' ? 'No issues yet!' : `No ${filter} issues`}
          </div>
          <div style={{ fontSize: 13, color: '#94a3b8' }}>Happy customers = no issues</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {issues.map(issue => (
            <div key={issue.id} className="card" style={{ marginBottom: 0 }}>
              <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                <span style={{ fontSize: 20, flexShrink: 0 }}>{PRIORITY_ICON[issue.priority]}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 4 }}>
                    <span className={`badge ${STATUS_BADGE[issue.status]}`}>{issue.status}</span>
                    <span className={`badge ${PRIORITY_BADGE[issue.priority]}`}>{issue.priority}</span>
                    <span style={{ fontSize: 11, color: '#94a3b8' }}>
                      {formatDistanceToNow(new Date(issue.created_at), { addSuffix: true })}
                    </span>
                    {issue.resolved_at && (
                      <span style={{ fontSize: 11, color: '#94a3b8' }}>
                        · Resolved {format(new Date(issue.resolved_at), 'dd MMM')}
                      </span>
                    )}
                  </div>
                  <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>{issue.title}</div>
                  <div style={{ fontSize: 12, color: '#64748b', marginBottom: 4 }}>
                    👤 {issue.customer_name}
                    {issue.customer_email && <> · {issue.customer_email}</>}
                    {issue.customer_whatsapp && (
                      <> ·{' '}
                        <a href={`https://wa.me/${issue.customer_whatsapp.replace(/\D/g,'')}`}
                          target="_blank" rel="noreferrer"
                          style={{ color: '#22c55e' }}>💬 WhatsApp</a>
                      </>
                    )}
                  </div>
                  {/* Expandable description */}
                  {expanded === issue.id && (
                    <div style={{ marginTop: 8, padding: 12, background: '#f8fafc', borderRadius: 8 }}>
                      <div style={{ fontSize: 13, color: '#374151', lineHeight: 1.7, marginBottom: 8 }}>
                        {issue.description || 'No description provided.'}
                      </div>
                      {issue.resolution_notes && (
                        <div style={{ fontSize: 12, color: '#16a34a', background: '#dcfce7', padding: '8px 12px', borderRadius: 6 }}>
                          <strong>Resolution:</strong> {issue.resolution_notes}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 6, flexShrink: 0, flexWrap: 'wrap' }}>
                  <button className="btn-sm"
                    onClick={() => setExpanded(expanded === issue.id ? null : issue.id)}>
                    {expanded === issue.id ? 'Hide ↑' : 'View ↓'}
                  </button>
                  {issue.status !== 'resolved' && (
                    <button className="btn-sm btn-green"
                      onClick={() => quickStatus(issue.id, 'resolved')}>✓</button>
                  )}
                  {issue.status === 'open' && (
                    <button className="btn-sm"
                      style={{ background: '#dbeafe', color: '#2563eb' }}
                      onClick={() => quickStatus(issue.id, 'in_progress')}>▶</button>
                  )}
                  <button className="btn-sm" onClick={() => openEdit(issue)}>✏️</button>
                  <button className="btn-sm" style={{ background: '#fee2e2', color: '#dc2626' }}
                    onClick={() => deleteIssue(issue.id)}>🗑️</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {modal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-title">{modal.mode === 'add' ? '+ New Issue' : '✏️ Edit Issue'}</div>
            <div className="modal-grid">
              <div><label>Customer Name *</label><input value={form.customer_name} onChange={e => setForm(f => ({...f, customer_name: e.target.value}))} placeholder="e.g. Rahul Gupta" /></div>
              <div><label>Customer Email</label><input value={form.customer_email} onChange={e => setForm(f => ({...f, customer_email: e.target.value}))} placeholder="rahul@email.com" /></div>
              <div><label>Customer WhatsApp</label><input value={form.customer_whatsapp} onChange={e => setForm(f => ({...f, customer_whatsapp: e.target.value}))} placeholder="+91 98765 43210" /></div>
              <div>
                <label>Priority</label>
                <select value={form.priority} onChange={e => setForm(f => ({...f, priority: e.target.value}))}>
                  {PRIORITY_OPTS.map(p => <option key={p} value={p}>{PRIORITY_ICON[p]} {p}</option>)}
                </select>
              </div>
              <div>
                <label>Status</label>
                <select value={form.status} onChange={e => setForm(f => ({...f, status: e.target.value}))}>
                  {STATUS_OPTS.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>
            <div style={{ marginBottom: 14 }}>
              <label>Issue Title *</label>
              <input value={form.title} onChange={e => setForm(f => ({...f, title: e.target.value}))} placeholder="e.g. Screenshots not loading in dashboard" />
            </div>
            <div style={{ marginBottom: 14 }}>
              <label>Description</label>
              <textarea rows={3} value={form.description} onChange={e => setForm(f => ({...f, description: e.target.value}))} placeholder="Describe the problem in detail…" />
            </div>
            <div style={{ marginBottom: 20 }}>
              <label>Resolution Notes</label>
              <textarea rows={2} value={form.resolution_notes} onChange={e => setForm(f => ({...f, resolution_notes: e.target.value}))} placeholder="How was this resolved?…" />
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button className="btn-sm" onClick={() => setModal(null)}>Cancel</button>
              <button className="btn" style={{ marginTop: 0 }} onClick={save} disabled={saving}>
                {saving ? 'Saving…' : modal.mode === 'add' ? 'Create Issue' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && <div className="toast">{toast}</div>}
    </div>
  );
}
